import React from 'react';

import * as log from 'Utilities/log';

type FallbackProps = {
  error: Error | undefined;
  reset: () => void;
};

type Props = React.PropsWithChildren & {
  FallbackComponent?: React.FC<React.PropsWithChildren<FallbackProps>>;
  fallbackRender?: (props: FallbackProps) => React.ReactNode;
};

type State = {
  error?: Error;
  hasError: boolean;
};

class ErrorBoundary extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false };
  }

  resetComponent = () => {
    this.setState({ hasError: false });
  };

  static getDerivedStateFromError(error: Error) {
    // Update state so the next render will show the fallback UI.
    return { error, hasError: true };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    // You can also log the error to an error reporting service
    log.error(error, errorInfo);
  }

  render() {
    const { children, FallbackComponent, fallbackRender } = this.props;
    const { error, hasError } = this.state;

    if (hasError) {
      if (FallbackComponent) {
        return <FallbackComponent error={error} reset={this.resetComponent} />;
      } else if (fallbackRender) {
        return fallbackRender({ error, reset: this.resetComponent });
      }

      return null;
    }

    return children;
  }
}

export default ErrorBoundary;
